import React from 'react';
import { Accordion, CmhcFlexIcon, CmhcIcon, StatusIndicators } from '@cmhc-schl/hearth-ui';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SpinnerElement } from '../ErrorMessage';
import { errorSeverityMapping } from '../HbtPortfolioLoansInventoryTable/utils/ErrorSeverityMapping';
import { useLoansErrors } from './hooks/useLoanErrors';
import RightPaneHeader from './RightPaneHeader';
import styles from './styles.module.scss';
import { RightPaneContentProps, SeverityCode } from './types';
import { ErrorListWithToast } from './utils/copyItemsToClipBoard';
import errorsMapping from './utils/errorsMapping';
import { groupErrorsBySeverity, sortSeverityKeys } from './utils/filterDataUtils';
import RenderCenteredContent from './utils/renderCenteredContent';

const RightPaneContent: React.FC<RightPaneContentProps> = ({
  handleCloseFlyout,
  subHeaderText,
  bodyContent,
  selectedRowSubHeaderData,
  noResultsBodyContent,
  moreThenOneIsSelectedContent,
  noErrorsContent,
  count,
  data
}) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { data: loans, loading } = useLoansErrors();

  const mappedCodes = errorsMapping(data[0], loans ?? []);
  const hasErrors = !!data[0]?.errorMessageInfo;

  const severityMapping = errorSeverityMapping(t);

  const renderLoadingState = () => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: '100px'
      }}
    >
      <Stack spacing={2} alignItems="center">
        <SpinnerElement />
      </Stack>
    </Box>
  );

  const renderNoResultsState = () =>
    RenderCenteredContent(
      <CmhcFlexIcon
        className={styles.transparentIcon}
        type="icon_docsearch"
        fill="transparent"
        aria-label={t('PI-Portfolio-Management-Loans-Details-No-Results-To-Display')}
        width={60}
        height={60}
      />,
      t('PI-Portfolio-Management-Loans-Details-No-Results-To-Display'),
      noResultsBodyContent
    );

  const renderEmptyDataState = () =>
    RenderCenteredContent(
      <CmhcFlexIcon
        className={styles.transparentIcon}
        type="icon_check_circle_colored"
        fill="transparent"
        aria-label={t('Pi-Portfolio-Management-Empty-Data-Error-Header')}
        width={60}
        height={60}
      />,
      t('Pi-Portfolio-Management-Empty-Data-Error-Header'),
      noErrorsContent
    );

  const renderMultipleLoansState = () =>
    RenderCenteredContent(
      <CmhcFlexIcon
        className={styles.transparentIcon}
        type="icon_list"
        fill="transparent"
        aria-label={t('PI-Portfolio-Management-Loans-Details-Multiple-Loans-Selected')}
        width={60}
        height={60}
      />,
      t('PI-Portfolio-Management-Loans-Details-Multiple-Loans-Selected'),
      moreThenOneIsSelectedContent
    );

  const renderErrorsAccordion = () => {
    let groupedErrors = groupErrorsBySeverity(mappedCodes ?? {});
    const severityKeys = Object.keys(groupedErrors) as SeverityCode[];

    if (severityKeys.length === 0) return null;
    const sortedKeys = sortSeverityKeys(severityKeys, severityMapping);
    return (
      <Box className={styles?.contentAccordion}>
        {sortedKeys.map((severityKey) => (
          <Accordion
            key={severityKey}
            accordionData={{
              contentSlot: <ErrorListWithToast mappedErrors={groupedErrors[severityKey]!} />,
              contentType: 1,
              defaultExpanded: true,
              heading: `${severityMapping[severityKey]} (${
                Object.keys(groupedErrors[severityKey]!).length
              })`
            }}
          />
        ))}
      </Box>
    );
  };

  const renderConditionalContent = () => {
    if (loading) return renderLoadingState();
    if (count === 0) return renderNoResultsState();
    if (!hasErrors && count === 1) return renderEmptyDataState();
    if (count >= 10 || (count > 1 && count < 10)) return renderMultipleLoansState();
    if (count === 1 && hasErrors) return renderErrorsAccordion();
    return null;
  };

  const headerProps = {
    count,
    subHeaderText,
    bodyContent,
    selectedRowSubHeaderData,
    t,
    handleCloseFlyout,
    data
  };

  return (
    <Box role="presentation" className={styles.contentWrapper}>
      <RightPaneHeader {...headerProps} />
      <Box className={styles.contentBody}>{renderConditionalContent()}</Box>
    </Box>
  );
};

export default RightPaneContent;
